import { useEffect, useState } from 'react';
import { FlaskConical, Check, Loader2, AlertCircle } from 'lucide-react';
import { getAdvancedAnalytics, verifyExperiment } from '../../services/analytics';
import { useToast } from '../../components/ui/Modal';

export default function ExperimentVerifier() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedTest, setSelectedTest] = useState('');
    const [controlGroup, setControlGroup] = useState([]);
    const [experimentGroup, setExperimentGroup] = useState([]);
    const [verifying, setVerifying] = useState(false);
    const [result, setResult] = useState(null);
    const toast = useToast();

    useEffect(() => {
        getAdvancedAnalytics()
            .then((res) => setData(res.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const toggleStudent = (id, group) => {
        const setter = group === 'control' ? setControlGroup : setExperimentGroup;
        setter((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const handleVerify = async () => {
        if (!selectedTest) {
            toast.error('Testni tanlang');
            return;
        }
        if (controlGroup.length < 1 || experimentGroup.length < 1) {
            toast.error('Har ikkala guruhda kamida bitta talaba bo\'lishi kerak');
            return;
        }

        setVerifying(true);
        setResult(null);
        try {
            const res = await verifyExperiment({
                control_group: controlGroup,
                experiment_group: experimentGroup,
                test_id: parseInt(selectedTest),
            });
            setResult(res.data);
            toast.success('Verifikatsiya muvaffaqiyatli bajarildi');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Hisoblashda xatolik');
        } finally {
            setVerifying(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 text-white">
                <h1 className="text-2xl font-bold flex items-center gap-2">
                    <FlaskConical className="w-7 h-7" /> Eksperiment verifikatsiyasi
                </h1>
                <p className="text-blue-100 mt-1">Nazorat va eksperiment guruhlari orasidagi farqning ilmiy ishonchliiligini χ² testi bilan tekshiring</p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700 space-y-5">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Testni tanlang</label>
                    <select
                        value={selectedTest}
                        onChange={(e) => setSelectedTest(e.target.value)}
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">Testni tanlang</option>
                        {data?.courses?.map((course) => (
                            <optgroup key={course.id} label={course.title}>
                                {course.modules?.map((mod) =>
                                    mod.lessons?.map((lesson) =>
                                        lesson.test ? (
                                            <option key={lesson.test.id} value={lesson.test.id}>
                                                {lesson.test.title}
                                            </option>
                                        ) : null
                                    )
                                )}
                            </optgroup>
                        ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                        Agar kurslar ro'yxatida testlar ko'rinmasa, avval adaptiv test yaratilgan kursga o'ting.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-gray-400" /> Nazorat guruhi
                            <span className="text-xs font-normal text-gray-500">({controlGroup.length} ta tanlandi)</span>
                        </h3>
                        <div className="border border-gray-200 dark:border-gray-700 rounded-xl h-48 overflow-y-auto p-2 space-y-1">
                            {data?.students?.map((student) => (
                                <label
                                    key={student.id}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                >
                                    <input
                                        type="checkbox"
                                        checked={controlGroup.includes(student.id)}
                                        onChange={() => toggleStudent(student.id, 'control')}
                                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">{student.name} <span className="text-gray-400">({student.email})</span></span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500" /> Eksperiment guruhi
                            <span className="text-xs font-normal text-gray-500">({experimentGroup.length} ta tanlandi)</span>
                        </h3>
                        <div className="border border-gray-200 dark:border-gray-700 rounded-xl h-48 overflow-y-auto p-2 space-y-1">
                            {data?.students?.map((student) => (
                                <label
                                    key={student.id}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                >
                                    <input
                                        type="checkbox"
                                        checked={experimentGroup.includes(student.id)}
                                        onChange={() => toggleStudent(student.id, 'experiment')}
                                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">{student.name} <span className="text-gray-400">({student.email})</span></span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <button
                    onClick={handleVerify}
                    disabled={verifying}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 transition"
                >
                    {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    {verifying ? 'Hisoblanmoqda...' : 'χ² testini boshlash'}
                </button>
            </div>

            {result && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700 space-y-4">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-blue-600" /> Natija
                    </h2>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                            <div className="text-sm text-gray-500 dark:text-gray-400">Nazorat guruhi</div>
                            <div className="text-lg font-bold text-gray-900 dark:text-gray-100 mt-1">
                                {result.table.control.passed} / {result.table.control.total}
                            </div>
                            <div className="text-xs text-gray-500">o'tgan</div>
                        </div>
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 text-center">
                            <div className="text-sm text-gray-500 dark:text-gray-400">Eksperiment guruhi</div>
                            <div className="text-lg font-bold text-gray-900 dark:text-gray-100 mt-1">
                                {result.table.experiment.passed} / {result.table.experiment.total}
                            </div>
                            <div className="text-xs text-gray-500">o'tgan</div>
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                            <div className="text-sm text-gray-500 dark:text-gray-400">χ² qiymati</div>
                            <div className="text-xl font-bold text-gray-900 dark:text-gray-100 mt-1">{result.statistics.chi2}</div>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                            <div className="text-sm text-gray-500 dark:text-gray-400">Erkinlik darajasi</div>
                            <div className="text-xl font-bold text-gray-900 dark:text-gray-100 mt-1">{result.statistics.degrees_of_freedom}</div>
                        </div>
                        <div className={`rounded-xl p-4 text-center ${result.statistics.significant ? 'bg-green-50 dark:bg-green-900/20' : 'bg-yellow-50 dark:bg-yellow-900/20'}`}>
                            <div className="text-sm text-gray-500 dark:text-gray-400">p-qiymat</div>
                            <div className={`text-xl font-bold mt-1 ${result.statistics.significant ? 'text-green-700 dark:text-green-300' : 'text-yellow-700 dark:text-yellow-300'}`}>
                                {result.statistics.p_value}
                            </div>
                        </div>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 text-sm text-blue-800 dark:text-blue-200">
                        {result.statistics.conclusion}
                    </div>
                </div>
            )}
        </div>
    );
}
